import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollowUser, useMyFollowsQuery } from '@/hooks/useFollowActions';
import { useToast } from '@/hooks/useToast';
import { displayNameOf, ProfileAvatar } from '@/components/Avatar';
import { shortPubkey } from '@/lib/format';
import { sanitizeUrl } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BadgeCheck, Check, Plus } from 'lucide-react';

export function MemberRow({ pubkey }: { pubkey: string }) {
  const { data, isLoading } = useAuthor(pubkey);
  const metadata = data?.metadata;
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { data: myFollows, refetch } = useMyFollowsQuery(Boolean(user));
  const followMutation = useFollowUser(pubkey);

  const isSelf = user?.pubkey === pubkey;
  const isFollowing = myFollows?.includes(pubkey) ?? false;
  const [copied, setCopied] = useState(false);

  const name = displayNameOf(metadata, pubkey);
  const npub = (() => {
    try {
      return nip19.npubEncode(pubkey);
    } catch {
      return pubkey;
    }
  })();

  const toggleFollow = async () => {
    if (!user) {
      toast({ title: 'Log in to follow', description: 'You need to be logged in to follow users.' });
      return;
    }
    await followMutation.mutateAsync(!isFollowing);
    await refetch();
    toast({
      title: isFollowing ? `Unfollowed ${name}` : `Following ${name}`,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2.5 py-3 sm:gap-3">
        <Skeleton className="size-10 shrink-0 rounded-full sm:size-12" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-4 w-24 sm:w-32" />
          <Skeleton className="h-3 w-32 sm:w-48" />
        </div>
        <Skeleton className="h-8 w-16 shrink-0 rounded-full sm:h-9 sm:w-24" />
      </div>
    );
  }

  const profileUrl = (() => {
    const safe = metadata?.website ? sanitizeUrl(metadata.website) : null;
    return safe;
  })();

  return (
    <div className="flex items-center gap-2.5 py-3 sm:gap-3">
      <div className="shrink-0">
        <ProfileAvatar pubkey={pubkey} size="md" className="sm:size-14 sm:text-lg" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-1.5 font-semibold">
          <span className="min-w-0 flex-1 truncate text-[15px]">{name}</span>
          {metadata?.nip05 ? (
            <BadgeCheck className="size-4 shrink-0 text-muted-foreground" aria-label={`Verified ${metadata.nip05}`} />
          ) : null}
        </p>
        <p className="truncate text-[13px] text-muted-foreground sm:text-sm">
          {metadata?.nip05 ?? shortPubkey(pubkey)}
        </p>
        {metadata?.about ? (
          <p className="mt-0.5 line-clamp-2 break-words text-[13px] text-muted-foreground sm:text-sm">{metadata.about}</p>
        ) : null}
        <div className="mt-1 flex min-w-0 items-center gap-2 text-xs">
          <button
            type="button"
            className="min-w-0 flex-1 truncate text-left text-muted-foreground underline-offset-4 hover:text-foreground hover:underline sm:flex-none"
            onClick={async () => {
              await navigator.clipboard.writeText(npub);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            title="Copy npub"
          >
            {copied ? 'Copied!' : `${npub.slice(0, 14)}…`}
          </button>
          {profileUrl ? (
            <a href={profileUrl} target="_blank" rel="noreferrer" className="shrink-0 underline-offset-4 hover:underline">
              website
            </a>
          ) : null}
        </div>
      </div>
      {!isSelf ? (
        <Button
          variant={isFollowing ? 'secondary' : 'default'}
          className="h-8 shrink-0 rounded-full px-2.5 text-xs sm:h-9 sm:px-4 sm:text-sm"
          size="sm"
          disabled={followMutation.isPending}
          onClick={toggleFollow}
        >
          {followMutation.isPending ? '…' : isFollowing ? (
            <>
              <Check className="size-3.5 sm:size-4" />
              <span className="hidden min-[400px]:inline">Following</span>
              <span className="min-[400px]:hidden">On</span>
            </>
          ) : (
            <>
              <Plus className="size-3.5 sm:size-4" />
              <span className="hidden min-[400px]:inline">Follow</span>
              <span className="min-[400px]:hidden">+</span>
            </>
          )}
        </Button>
      ) : (
        <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1.5 text-xs font-medium text-secondary-foreground">
          You
        </span>
      )}
    </div>
  );
}

export function MemberListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <Card className="min-w-0">
      <CardContent className="divide-y px-3 sm:px-5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-2.5 py-3 sm:gap-3">
            <Skeleton className="size-10 shrink-0 rounded-full sm:size-12" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-24 sm:w-32" />
              <Skeleton className="h-3 w-32 sm:w-48" />
            </div>
            <Skeleton className="h-8 w-16 shrink-0 rounded-full sm:h-9 sm:w-24" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
